import { EntityIconMd } from '@components/core-ui/EntityIcon/variations/EntityIconMd';
import TrashBin from '@components/icons/TrashBin.svg';
import { uploadOrgImage } from '@helpers/uploadS3Image';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation } from '@tanstack/react-query';
import { OrganizationsService } from 'app/API';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { FilePicker } from '@components/FilePicker';
import toast from 'react-hot-toast';

import { OrgImageBlock } from './styles/OrgImageBlock';

export const OrganizationImage = () => {
  const { call } = useAPI();
  const { openModal, closeModal } = useModalContext();
  const { updateMe, organization } = useAuthContext();

  const submitOrgImage = (file: File) => {
    if (!organization) {
      return;
    }
    openModal(ModalNamesEnum.ImageUploadModal, {
      file,
      onSuccess: async (orgImage: string) => {
        try {
          await uploadOrgImage(organization.id, orgImage);
          await updateMe();
        } catch (uploadError) {
          toast.error(uploadError instanceof Error ? uploadError.message : 'Could not upload the icon.');
        }
        closeModal();
      },
    });
  };

  const removeImageMutation = useMutation({
    mutationFn: (organizationId: string) =>
      call(() => OrganizationsService.removeImage({ oid: organizationId }), {
        onError: (message) => toast.error(message || 'Unable to remove the icon.'),
      }),
    onSuccess: () => updateMe(),
  });

  const removeOrgImage = () => {
    if (!organization) {
      return;
    }
    removeImageMutation.mutate(organization.id);
  };

  if (!organization) {
    return <OrgImageBlock />;
  }

  return (
    <OrgImageBlock>
      <EntityIconMd name={organization.name} src={organization.image ?? undefined} />
      <FilePicker accept="image/jpeg,image/png" onFile={submitOrgImage}>
        <OutlinedButton className="w-32" type="button">
          Upload icon
        </OutlinedButton>
      </FilePicker>
      {organization.image ? (
        <IconButtonDelete title="Remove" disabled={removeImageMutation.isPending} onClick={removeOrgImage}>
          <TrashBin />
        </IconButtonDelete>
      ) : null}
    </OrgImageBlock>
  );
};

export const OrganizationImageViewOnly = () => {
  const { organization } = useAuthContext();

  if (!organization) {
    return <OrgImageBlock />;
  }

  return (
    <OrgImageBlock>
      <EntityIconMd name={organization.name} src={organization.image ?? undefined} />
    </OrgImageBlock>
  );
};
